"use client";

import { useEffect, useState, useRef } from "react";
import Image from "next/image";
import { cn } from "@/lib/utils";
import { getRandomPhrase } from "@/data/loading-phrases";
import { getStageLabel, type StageName } from "@/data/loading-stages";
import { useTranslations } from "@/lib/use-translations";
import { WhackAMole } from "./whack-a-mole";
import { FlappyBird } from "./flappy-bird";
import type { LocaleCode, Richness } from "@/lib/api/client";

// Fun waiting GIFs - rotate through these during long waits
const WAIT_GIFS = [
  "/cookie-monster-waiting.webp",
  "/goldie-laptop-type.webp",
  "/cat-reading-book.webp",
];

const FINISHING_GIF = "/penguin-running-to-workl.webp";

const GIF_START_DELAY = 0; // Show first GIF immediately (set to 15000 for production)
const GIF_ROTATE_INTERVAL = 10000; // Each GIF shows for 10 seconds

interface GenerationProgressProps {
  stage: number;
  total: number;
  stageName: StageName;
  nativeLocale: LocaleCode;
  detail?: string;
  richness?: Richness;
  onGameActive?: (active: boolean) => void;
  streamingNodeCount?: number;
}

// Time-based stages fill first 7 dots over a duration that depends on richness
const TIME_BASED_STAGES: StageName[] = ["generating", "streaming", "structuring"];
const TIME_BASED_MAX_DOTS = 7;
const RICHNESS_DURATION: Record<string, number> = {
  compact: 17000,     // ~17 seconds for flash-only
  standard: 40000,    // ~40 seconds for hybrid
  comprehensive: 70000, // ~70 seconds for full
};

export function GenerationProgress({
  stage,
  total,
  stageName,
  nativeLocale,
  detail,
  richness = "standard",
  onGameActive,
  streamingNodeCount,
}: GenerationProgressProps) {
  const [goofyPhrase, setGoofyPhrase] = useState(() =>
    getRandomPhrase(nativeLocale)
  );
  const [currentGif, setCurrentGif] = useState<string | null>(null);
  const startTimeRef = useRef<number>(Date.now());
  const [timerDots, setTimerDots] = useState(1);
  const [activeGame, setActiveGame] = useState<"none" | "whack" | "flappy">("none");

  // Notify parent when game state changes
  useEffect(() => {
    onGameActive?.(activeGame !== "none");
  }, [activeGame, onGameActive]);

  // Time-based progress for early stages
  const duration = RICHNESS_DURATION[richness] ?? RICHNESS_DURATION.standard!;
  useEffect(() => {
    if (!TIME_BASED_STAGES.includes(stageName)) return;

    const interval = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);
      // Ease-out: fast start, slows down toward the end
      const eased = 1 - Math.pow(1 - progress, 2);
      const dots = Math.max(1, Math.round(eased * TIME_BASED_MAX_DOTS));
      setTimerDots(dots);
    }, 500);

    return () => clearInterval(interval);
  }, [stageName, duration]);

  // Compute display stage: during time-based stages use the higher of timer or actual
  const isTimeBased = TIME_BASED_STAGES.includes(stageName);
  const displayStage = isTimeBased
    ? Math.max(timerDots, stage)
    : Math.max(TIME_BASED_MAX_DOTS, stage);

  // Rotate goofy phrases
  useEffect(() => {
    const interval = setInterval(() => {
      setGoofyPhrase(getRandomPhrase(nativeLocale));
    }, 4500);

    return () => clearInterval(interval);
  }, [nativeLocale]);

  // Show and rotate GIFs after initial delay
  useEffect(() => {
    // Special case: show penguin when finishing
    if (stageName === "finishing") {
      setCurrentGif(FINISHING_GIF);
      return;
    }

    const checkTime = () => {
      const elapsed = Date.now() - startTimeRef.current;

      if (elapsed < GIF_START_DELAY) {
        setCurrentGif(null);
        return;
      }

      // Calculate which GIF to show based on elapsed time
      const timeSinceStart = elapsed - GIF_START_DELAY;
      const gifIndex = Math.floor(timeSinceStart / GIF_ROTATE_INTERVAL) % WAIT_GIFS.length;
      setCurrentGif(WAIT_GIFS[gifIndex] ?? null);
    };

    // Check immediately and then every second
    checkTime();
    const interval = setInterval(checkTime, 1000);

    return () => clearInterval(interval);
  }, [stageName]);

  const { generator: gen } = useTranslations();
  const stageLabel = getStageLabel(nativeLocale, stageName);

  return (
    <div
      data-testid="generation-progress"
      className="flex flex-col items-center justify-center gap-4"
    >
      <div data-testid="stage-name" className="text-lg font-medium text-foreground">
        {stageLabel}
      </div>

      <div className="flex flex-col items-center gap-1">
        {detail && (
          <div
            data-testid="progress-detail"
            className="text-sm text-muted-foreground"
          >
            {detail}
          </div>
        )}
        <div
          data-testid="goofy-phrase"
          className="h-5 text-sm text-muted-foreground/70 italic"
        >
          {goofyPhrase}
        </div>
      </div>

      <div data-testid="progress-dots" className="flex gap-2">
        {Array.from({ length: total }, (_, i) => (
          <div
            key={i}
            className={cn(
              "w-3 h-3 rounded-full transition-colors duration-300",
              i < displayStage
                ? "animate-rainbow-dot"
                : "bg-muted"
            )}
            style={
              i < displayStage
                ? { animationDelay: `${i * 150}ms` }
                : undefined
            }
          />
        ))}
      </div>

      {/* Active game or waiting GIF */}
      {activeGame !== "none" ? (
        <div className="mt-4 flex flex-col items-center">
          {activeGame === "whack" ? <WhackAMole /> : <FlappyBird />}
          <div className="flex items-center gap-2 mt-6">
            <button
              type="button"
              onClick={() => setActiveGame(activeGame === "whack" ? "flappy" : "whack")}
              className="px-3 py-1.5 text-sm rounded-full bg-muted/80 text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-200"
            >
              {activeGame === "whack" ? "🦕 Dino Runner" : "🐹 Whack-a-Mole"}
            </button>
            <button
              type="button"
              onClick={() => setActiveGame("none")}
              className="px-3 py-1.5 text-xs rounded-full text-muted-foreground/50 hover:text-muted-foreground transition-colors"
            >
              {gen.exitGame}
            </button>
          </div>
        </div>
      ) : (
        <>
          {currentGif && (
            <div className="w-32 h-32 relative rounded-lg overflow-hidden mt-4">
              <Image
                src={currentGif}
                alt="Please wait..."
                fill
                className="object-cover"
                unoptimized
              />
            </div>
          )}
          <div className="flex items-center gap-1.5 mt-3 text-sm text-muted-foreground/70 flex-wrap justify-center">
            <span>{gen.play}</span>
            <button
              type="button"
              onClick={() => setActiveGame("whack")}
              className="px-2.5 py-1 rounded-full bg-muted/80 text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-200"
            >
              🐹 Whack-a-Mole
            </button>
            <span>{gen.or}</span>
            <button
              type="button"
              onClick={() => setActiveGame("flappy")}
              className="px-2.5 py-1 rounded-full bg-muted/80 text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-200"
            >
              🦕 Dino Runner
            </button>
            <span>{gen.whileYouWait}</span>
          </div>
        </>
      )}
    </div>
  );
}
